import { useCallback } from 'react';
import type * as Y from 'yjs';
import { useReactFlow, getNodesBounds, getViewportForBounds } from '@xyflow/react';
import { toPng } from 'html-to-image';
import { useCanvasPreviewMarkup } from './use-canvas-preview-markup';

/** Thumbnail dimensions for the captured preview (kept small — it's a card). */
const THUMB_WIDTH = 480;
const THUMB_HEIGHT = 360;
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 1;

export interface CanvasPreviewSnapshotterProps {
  doc: Y.Doc | null;
  documentId: string;
  /** Disabled for read-only viewers (they don't write previews). */
  enabled: boolean;
}

/**
 * Drives the canvas card-preview markup. Mounts INSIDE CanvasEngine (needs the
 * React Flow context) and feeds {@link useCanvasPreviewMarkup} a snapshot fn
 * that captures the rendered scene to a PNG via html-to-image, framed to the
 * content bounds (not the user's current pan/zoom).
 *
 * Highest-fidelity option: it captures the actual rendered DOM (custom nodes,
 * images/gifs, embedded entity cards, freehand strokes) rather than
 * re-serializing the scene — so it always matches what the user sees and never
 * drifts as node components change.
 *
 * NOTE: the snapshot path (html-to-image + RF viewport DOM) is not unit-tested
 * — it needs a real rendered canvas. The hook's debounce/dedup/gate logic IS
 * tested (use-canvas-preview-markup.test.ts) via an injected snapshot.
 */
export function CanvasPreviewSnapshotter({ doc, documentId, enabled }: CanvasPreviewSnapshotterProps) {
  const { getNodes } = useReactFlow();

  const snapshot = useCallback(async (): Promise<string> => {
    const nodes = getNodes();
    if (nodes.length === 0) return '';
    const viewportEl = document.querySelector<HTMLElement>('.react-flow__viewport');
    if (!viewportEl) return '';

    // Frame the capture to the content bounds at a thumbnail size, ignoring the
    // user's current pan/zoom.
    const bounds = getNodesBounds(nodes);
    const { x, y, zoom } = getViewportForBounds(
      bounds,
      THUMB_WIDTH,
      THUMB_HEIGHT,
      MIN_ZOOM,
      MAX_ZOOM,
      0.1, // padding
    );

    try {
      return await toPng(viewportEl, {
        backgroundColor: 'transparent',
        width: THUMB_WIDTH,
        height: THUMB_HEIGHT,
        style: {
          width: `${THUMB_WIDTH}px`,
          height: `${THUMB_HEIGHT}px`,
          transform: `translate(${x}px, ${y}px) scale(${zoom})`,
        },
        // External images (gifs, embedded media) need CORS fetch to avoid a
        // tainted-canvas failure; skip ones that can't be fetched rather than
        // failing the whole capture.
        skipFonts: false,
        cacheBust: true,
      });
    } catch {
      return ''; // capture failed (e.g. tainted image) — skip this update
    }
  }, [getNodes]);

  useCanvasPreviewMarkup(doc, { documentId, enabled, snapshot });
  return null;
}
